/**
 * Activity marker color constants
 * Used across all glucose charts for consistent activity visualization
 */

import { TONE_ACCENT } from '../themes/statusColors'
import { getCanonicalActivityType } from './activityType'

export const ACTIVITY_MARKER_COLORS: Record<string, string> = {
  food: '#FFD700', // Yellow for Feeding
  feeding: '#FFD700', // Yellow for Feeding
  // vic/vetpo#15: structured meal-status markers (FeedingEvents.outcome) —
  // same food-related visual bucket as food/feeding.
  meal: '#FFD700',
  insulin: '#FF0000', // Red for Insulin (also structured InsulinEvents doses)
  // vic/vetpo#15: "alternative instruction triggered" exception marker.
  // Deliberately distinct from routine insulin's red — TONE_ACCENT.alert
  // (UI style guide: loud color is reserved for abnormal/needs-attention).
  exception: TONE_ACCENT.alert,
  medicine: '#9C27B0', // Material Purple for Medicine/medication
  medication: '#9C27B0',
  exercise: '#28A745', // Green for Exercise
  water: '#007BFF', // Blue for Water Intake
  water_intake: '#007BFF', // Blue for Water Intake
  urination: '#FFA500', // Orange for Urination
  vomiting: '#800080', // Darker purple for Vomiting
  bgm: '#E91E63', // Pink for Blood Glucose Monitoring
  note: '#808080', // Gray for Notes
  default: '#808080', // Gray for Other
}

/**
 * Get activity marker color based on activity type
 * @param activityType - The type of activity (case-insensitive)
 * @returns Hex color code for the activity marker
 */
export const getActivityMarkerColor = (activityType: string): string => {
  const type = getCanonicalActivityType(activityType)
  return ACTIVITY_MARKER_COLORS[type] || ACTIVITY_MARKER_COLORS.default
}

/**
 * Activity priority for determining marker color when multiple events occur
 * Lower numbers = higher priority
 */
export const ACTIVITY_PRIORITY: Record<string, number> = {
  // vic/vetpo#15: an exception marker always shares its minute with the
  // insulin dose/skip marker it qualifies (same InsulinEvent row) — it must
  // win the concurrent-group color so the vet sees it, not the routine dose.
  // 0.5, not 0: byActivityPriority's `ACTIVITY_PRIORITY[...] || 8` fallback
  // treats a real 0 as falsy and would silently reassign it to 8.
  exception: 0.5,
  insulin: 1,
  feeding: 2,
  food: 2,
  meal: 2,
  vomiting: 3,
  exercise: 4,
  urination: 5,
  water: 6,
  water_intake: 6,
  bgm: 7,
  note: 8,
  default: 9,
}
